import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import './UserManagement.css';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user' });
  const [passwordChange, setPasswordChange] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === 'admin') {
      loadUsers();
    }
  }, [user]);

  const loadUsers = async () => {
    try {
      const response = await axios.get('/api/auth/users');
      setUsers(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Benutzer');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await axios.post('/api/auth/users', newUser);
      setSuccess('Benutzer erfolgreich erstellt');
      setNewUser({ username: '', password: '', role: 'user' });
      setShowAddUser(false);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;

    try {
      await axios.delete(`/api/auth/users/${id}`);
      setSuccess('Benutzer gelöscht');
      loadUsers();
    } catch (err) {
      setError('Fehler beim Löschen');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordChange.newPassword !== passwordChange.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    try {
      await axios.post('/api/auth/change-password', {
        currentPassword: passwordChange.currentPassword,
        newPassword: passwordChange.newPassword
      });
      setSuccess('Passwort erfolgreich geändert');
      setPasswordChange({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowChangePassword(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Ändern');
    }
  };

  return (
    <div className="user-management">
      <div className="page-header">
        <h1>Benutzerverwaltung</h1>
        <div className="header-actions">
          <button onClick={() => setShowChangePassword(true)} className="btn-secondary">
            Passwort ändern
          </button>
          {user?.role === 'admin' && (
            <button onClick={() => setShowAddUser(true)} className="btn-primary">
              + Benutzer hinzufügen
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {user?.role === 'admin' && (
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Rolle</th>
                <th>Erstellt</th>
                <th>Letzter Login</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>{u.username}</td>
                  <td>
                    <span className={`role-badge role-${u.role}`}>
                      {u.role === 'admin' ? 'Admin' : 'Benutzer'}
                    </span>
                  </td>
                  <td>{new Date(u.created_at).toLocaleDateString('de-DE')}</td>
                  <td>{u.last_login ? new Date(u.last_login).toLocaleDateString('de-DE') : '-'}</td>
                  <td>
                    {u.id !== user.id && (
                      <button onClick={() => handleDeleteUser(u.id)} className="btn-delete">
                        Löschen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddUser && (
        <div className="modal-overlay" onClick={() => setShowAddUser(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Neuer Benutzer</h2>
            <form onSubmit={handleAddUser}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Passwort</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Rolle</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="user">Benutzer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddUser(false)} className="btn-secondary">
                  Abbrechen
                </button>
                <button type="submit" className="btn-primary">
                  Erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showChangePassword && (
        <div className="modal-overlay" onClick={() => setShowChangePassword(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Passwort ändern</h2>
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label>Aktuelles Passwort</label>
                <input
                  type="password"
                  value={passwordChange.currentPassword}
                  onChange={(e) => setPasswordChange({ ...passwordChange, currentPassword: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Neues Passwort</label>
                <input
                  type="password"
                  value={passwordChange.newPassword}
                  onChange={(e) => setPasswordChange({ ...passwordChange, newPassword: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Passwort bestätigen</label>
                <input
                  type="password"
                  value={passwordChange.confirmPassword}
                  onChange={(e) => setPasswordChange({ ...passwordChange, confirmPassword: e.target.value })}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowChangePassword(false)} className="btn-secondary">
                  Abbrechen
                </button>
                <button type="submit" className="btn-primary">
                  Ändern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
